<template>
  <span></span>
</template>
<script setup lang="ts">
import { computed, ref, watchEffect } from "vue";
import Smooch from "smooch";
import { useRoute } from "vue-router";
import { useStore } from "vuex";

import { getChatBotConfiguration } from "../../services/chatbot-config.service";
import { getLanguageName } from "@/apps/person-registration/shared-helpers/language.helper";

const route = useRoute();
const integrationId = ref("");
const chatBotConfigAttempt = ref(false);
const registrationTag = computed(() => {
  return route.params.userTag || "";
});

const store = useStore();
const originatingCountry = computed(
  () => store.state.personRegistration.userData.originatingCountry
);
const isOriginatingCountryLoaded = ref(false);

const defaultChatBotParams = computed(() => {
  return {
    name: `Registration tag: ${registrationTag.value}`,
    country: originatingCountry.value,
    userType: "personRegistration",
    language: getLanguageName(),
  };
});

const chatBotParams = computed(() => {
  return chatBotMetaData.value;
});

const isChatbotInitialized = () => {
  let iframes = document.getElementsByTagName("iframe");
  for (let i = iframes.length - 1; i >= 0; i--) {
    if (iframes[i].id === "web-messenger-container") {
      return true;
    }
  }
  return false;
};

const setIntegrationId = () => {
  switch (originatingCountry.value) {
    case "MX":
      integrationId.value =
        process.env.VUE_APP_CHATBOT_PM_SMOOCH_MX_INTEGRATION_ID;
      break;
    case "EC":
      integrationId.value =
        process.env.VUE_APP_CHATBOT_PM_SMOOCH_EC_INTEGRATION_ID;
      break;
    case "ID":
      integrationId.value =
        process.env.VUE_APP_CHATBOT_PM_SMOOCH_ID_INTEGRATION_ID;
      break;
    case "CO":
      integrationId.value =
        process.env.VUE_APP_CHATBOT_PM_SMOOCH_CO_INTEGRATION_ID;
      break;
    case "ZA":
      integrationId.value =
        process.env.VUE_APP_CHATBOT_PM_SMOOCH_ZA_INTEGRATION_ID;
      break;
    case "BR":
      integrationId.value =
        process.env.VUE_APP_CHATBOT_PM_SMOOCH_BR_INTEGRATION_ID;
      break;
    case "PA":
      integrationId.value =
        process.env.VUE_APP_CHATBOT_PM_SMOOCH_PA_INTEGRATION_ID;
      break;
    case "PE":
      integrationId.value =
        process.env.VUE_APP_CHATBOT_PM_SMOOCH_PE_INTEGRATION_ID;
      break;
    case "PH":
      integrationId.value =
        process.env.VUE_APP_CHATBOT_PM_SMOOCH_PH_INTEGRATION_ID;
      break;
    default:
      integrationId.value = "";
      break;
  }
};
const addSmooch = (params) => {
  isChatbotInitialized() && Smooch.destroy();
  Smooch.init({
    integrationId: integrationId.value,
    locale: params.language,
    canUserSeeConversationList: false,
    notificationChannelPromptEnabled: false,
    browserStorage: "sessionStorage",
    integrationOrder: [],
  }).then(function () {
    Smooch.updateUser({
      givenName: params.name,
      surname: "",
      email: params.email,
      metadata: params,
    });
  });
};

const chatBotMetaData = ref();
const initiateChatBotWithDefaultMetaData = () => {
  if (isOriginatingCountryLoaded.value) {
    setIntegrationId();
    chatBotMetaData.value = defaultChatBotParams.value;
    addSmooch(chatBotParams.value);
  }
};

const fetchChatBotConfig = async (userTag: string): Promise<void> => {
  chatBotConfigAttempt.value = true;
  try {
    const response = await getChatBotConfiguration(userTag);
    if (response.valid) {
      chatBotMetaData.value = response.data?.configs?.metadata;
      integrationId.value = response.data?.configs?.integrationId ?? "";
      addSmooch(chatBotParams.value);
    } else {
      initiateChatBotWithDefaultMetaData();
    }
  } catch {
    initiateChatBotWithDefaultMetaData();
  }
};

watchEffect(async () => {
  isOriginatingCountryLoaded.value =
    store.state.personRegistration.userData.originatingCountry !== null;

  if (
    registrationTag.value &&
    !isChatbotInitialized() &&
    !chatBotConfigAttempt.value
  ) {
    await fetchChatBotConfig(registrationTag.value as string);
  }
});
</script>
